import { useState } from "react";
import PageHero from "@/components/PageHero";
import api, { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Phone, Mail, MapPin, Globe, Send, CheckCircle2, AlertCircle, Facebook, Instagram } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setErr("");
    try { await api.post("/contact", form); setDone(true); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Federation Secretariat" subtitle="For affiliation, championship registration, or any official enquiry — reach out to the NKF Bharat secretariat." />
      <section className="section-spacing nkf-container grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-4">
          <ContactCard icon={Phone} title="Federation Helpline">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {BRAND.contactPhones.map((p, i) => (
                <span key={p} className="inline-flex items-center gap-2">
                  {i > 0 && <span className="text-slate-300">·</span>}
                  <a href={`tel:${p.replace(/\s+/g, "")}`} className="hover:text-nkf-red transition" data-testid={`contact-phone-${i}`}>{p}</a>
                </span>
              ))}
            </div>
          </ContactCard>
          <ContactCard icon={Mail} title="Official Email">
            <a href={`mailto:${BRAND.contactEmail}`} className="hover:text-nkf-red transition" data-testid="contact-email-link">{BRAND.contactEmail}</a>
          </ContactCard>
          <ContactCard icon={Globe} title="Website">
            <a href={`https://${BRAND.website}`} target="_blank" rel="noreferrer" className="hover:text-nkf-red transition" data-testid="contact-website-link">{BRAND.website}</a>
          </ContactCard>
          <ContactCard icon={MapPin} title="Head Office">
            <a
              href="https://www.google.com/maps/search/?api=1&query=New+Delhi+India"
              target="_blank"
              rel="noreferrer"
              className="hover:text-nkf-red transition"
              data-testid="contact-address-link"
            >New Delhi, India</a>
          </ContactCard>
          <div className="bg-nkf-navy text-white rounded-md p-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Follow the Federation</div>
            <div className="mt-3 flex items-center gap-3">
              <a href="https://www.facebook.com/NationalKarateFederation/" target="_blank" rel="noreferrer" data-testid="contact-social-facebook" className="h-10 w-10 rounded-full bg-white/5 hover:bg-nkf-gold hover:text-nkf-navy inline-flex items-center justify-center transition" aria-label="Facebook"><Facebook className="h-4 w-4" /></a>
              <a href="https://www.instagram.com/nkf_national_karate_federation" target="_blank" rel="noreferrer" data-testid="contact-social-instagram" className="h-10 w-10 rounded-full bg-white/5 hover:bg-nkf-gold hover:text-nkf-navy inline-flex items-center justify-center transition" aria-label="Instagram"><Instagram className="h-4 w-4" /></a>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          {done ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-8 rounded-md text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
              <h3 className="font-display text-2xl mt-3">Message received</h3>
              <p className="text-sm mt-2">The federation secretariat will respond within 3 working days.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="bg-white border border-slate-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
              {err && <div className="md:col-span-2 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
              <Field label="Your Name *"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="contact-name" /></Field>
              <Field label="Email *"><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="contact-email" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="contact-phone" /></Field>
              <Field label="Subject *"><input required value={form.subject} onChange={(e) => set("subject", e.target.value)} className={inp} data-testid="contact-subject" /></Field>
              <Field label="Message *" wide><textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={inp} data-testid="contact-message" /></Field>
              <div className="md:col-span-2 flex justify-end">
                <button disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-nkf-blue text-white uppercase tracking-widest text-sm font-bold px-6 py-3 rounded hover:bg-nkf-navy disabled:opacity-60 transition">
                  {submitting ? "Sending…" : <>Send Message <Send className="h-4 w-4" /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue";
function Field({ label, children, wide }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="block text-[12px] uppercase tracking-widest text-slate-600 mb-1.5 font-semibold">{label}</span>
      {children}
    </label>
  );
}
function ContactCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 federation-card">
      <Icon className="h-5 w-5 text-nkf-red" />
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-3 font-semibold">{title}</div>
      <div className="font-display text-lg text-nkf-blue mt-1">{children}</div>
    </div>
  );
}
